import { useState, useMemo } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useApp } from '../../contexts/AppContext';
import { supabase } from '../../supabaseClient';

const ROLE_COLOR = { Admin:'#e05757', Operator:'#f5a623', Viewer:'#5b9cf6' };
const ROLE_DESC  = { Admin:'Full access — manage users, approve requests, configure system', Operator:'Can check-in/out assets, export reports, submit requests', Viewer:'Read-only access — view assets, tracking and reports only' };
const DEFAULT_PERMS = {
  Admin: {
    canViewAssets:true, canRegisterAssets:true, canEditAssets:true, canDeleteAssets:true,
    canCheckout:true, canApprove:true, canViewTracking:true,
    canViewFacilities:true, canManageFacilities:true,
    canViewReports:true, canExport:true,
    canManageUsers:true, canConfig:true, canDelete:true,
  },
  Operator: {
    canViewAssets:true, canRegisterAssets:true, canEditAssets:true, canDeleteAssets:false,
    canCheckout:true, canApprove:false, canViewTracking:true,
    canViewFacilities:true, canManageFacilities:false,
    canViewReports:true, canExport:true,
    canManageUsers:false, canConfig:false, canDelete:false,
  },
  Viewer: {
    canViewAssets:true, canRegisterAssets:false, canEditAssets:false, canDeleteAssets:false,
    canCheckout:false, canApprove:false, canViewTracking:true,
    canViewFacilities:true, canManageFacilities:false,
    canViewReports:true, canExport:false,
    canManageUsers:false, canConfig:false, canDelete:false,
  },
};

// Grouped permission definitions with page tags
const PERM_GROUPS = [
  {
    group: 'Assets', icon: '📦',
    perms: [
      { key:'canViewAssets',      label:'View Assets',          desc:'Browse and search the asset registry',      pages:['📦 Assets'] },
      { key:'canRegisterAssets',  label:'Register Assets',      desc:'Add new assets to the registry',            pages:['📦 Assets → Register'] },
      { key:'canEditAssets',      label:'Edit Asset Details',   desc:'Update asset info, notes and status',       pages:['📦 Assets → Edit'] },
      { key:'canDeleteAssets',    label:'Delete / Archive Assets', desc:'Permanently remove or archive records',  pages:['📦 Assets → Delete'] },
    ],
  },
  {
    group: 'Operations', icon: '🔄',
    perms: [
      { key:'canCheckout',    label:'Check In / Out',         desc:'Issue and return assets, manage deliveries',    pages:['🔄 Checkout','🚚 Deliveries'] },
      { key:'canApprove',     label:'Approve Requests',       desc:'Approve, reject and complete transfer requests', pages:['📋 Requests → Approve'] },
      { key:'canViewTracking',label:'Live Tracking',          desc:'View real-time RFID asset tracking map',        pages:['📡 Live Tracking'] },
    ],
  },
  {
    group: 'Facilities', icon: '🏭',
    perms: [
      { key:'canViewFacilities',   label:'View Facilities',    desc:'Browse facilities and their details',         pages:['🏭 Facilities'] },
      { key:'canManageFacilities', label:'Manage Facilities',  desc:'Add, edit and configure facility settings',   pages:['🏭 Facilities → Edit'] },
    ],
  },
  {
    group: 'Reports', icon: '📈',
    perms: [
      { key:'canViewReports', label:'View Reports',  desc:'Access analytics and movement history',    pages:['📈 Reports'] },
      { key:'canExport',      label:'Export Data',   desc:'Download reports as CSV or PDF',           pages:['📈 Reports → Export'] },
    ],
  },
  {
    group: 'Administration', icon: '⚙',
    perms: [
      { key:'canManageUsers', label:'Manage Users',      desc:'Create, edit and deactivate user accounts', pages:['👥 Users'] },
      { key:'canConfig',      label:'System Settings',   desc:'Configure system-wide settings',            pages:['⚙ Settings'] },
    ],
  },
];

function Lbl({ children }) { return <div style={{ fontSize:10,color:"#7090a0",textTransform:"uppercase",letterSpacing:"1px",fontWeight:600,marginBottom:6 }}>{children}</div>; }
function Modal({ title, sub, onClose, children }) {
  return <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.88)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:"#090e15",border:"1px solid #1a2a3a",borderRadius:14,width:"100%",maxWidth:560,maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column" }}>
      <div style={{ padding:"20px 24px",borderBottom:"1px solid #1a2a3a" }}>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:"#e8edf2" }}>{title}</div>
        {sub && <div style={{ fontSize:11,color:"#7090a0",marginTop:3 }}>{sub}</div>}
      </div>
      <div style={{ padding:24,overflowY:"auto",flex:1 }}>{children}</div>
    </div>
  </div>;
}

export default function UsersPage() {
  const { users, setUsers, profile, showToast } = useApp();
  const { features } = useTenant();
  const _f = features || {};

  // Filter permission rows based on enabled features
  const visiblePermRows = PERM_GROUPS.map(group => ({
    ...group,
    perms: group.perms.filter(p => {
      if (p.key === 'canCheckout' && (_f.can_checkout === false || _f.can_deliveries === false)) return false;
      if (p.key === 'canApprove'  && _f.can_requests  === false) return false;
      return true;
    })
  })).filter(g => g.perms.length > 0);
  const [search,setSearch]         = useState("");
  const [roleFilter,setRoleFilter] = useState("All");
  const [rolePerms,setRolePerms]   = useState(DEFAULT_PERMS);
  const [modal,setModal]           = useState(null);
  const [deleteConfirm,setDeleteConfirm] = useState(null);
  const [form,setForm]             = useState({});
  const [showPass,setShowPass]     = useState(false);
  const [saving,setSaving]         = useState(false);

  const filtered = useMemo(()=>{
    let d = users;
    if (search) { const q=search.toLowerCase(); d=d.filter(u=>u.name?.toLowerCase().includes(q)||u.email?.toLowerCase().includes(q)); }
    if (roleFilter!=="All") d=d.filter(u=>u.role===roleFilter);
    return d;
  },[users,search,roleFilter]);

  const initials = (name="") => name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();

  const openAdd = () => { setForm({ name:"", email:"", role:"Operator" }); setModal("add"); };
  const openEdit = (u) => { setForm({ ...u, password:"" }); setModal(u); };

  const saveAdd = async () => {
    if (!form.name || !form.email) return;
    setSaving(true);
    try {
      const { supabaseAdmin } = await import('../../supabaseClient');
      if (!supabaseAdmin) throw new Error('REACT_APP_SUPABASE_SERVICE_KEY missing in .env file');

      // 1. Send invite email — user sets their own password
      const { data: invData, error: invErr } = await supabaseAdmin.auth.admin.inviteUserByEmail(
        form.email,
        { data: { name: form.name } }
      );
      if (invErr) throw new Error(invErr.message);

      const userId = invData.user.id;

      // 2. Create profile in same org as admin
      const { error: profErr } = await supabaseAdmin
        .from('profiles')
        .upsert({
          id:         userId,
          email:      form.email,
          name:       form.name,
          role:       form.role || 'Operator',
          org_id:     profile?.org_id,
          status:     'Active',
          created_at: new Date().toISOString(),
        }, { onConflict: 'id' });
      if (profErr) throw new Error(profErr.message);

      setUsers(prev => [...prev, {
        id: userId, email: form.email, name: form.name,
        role: form.role, org_id: profile?.org_id, status: 'Active'
      }]);
      showToast(`✓ Invite sent to ${form.email} — they will receive an email to set their password`);
      setModal(null);
    } catch (err) { showToast('❌ ' + err.message); }
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      await supabase.from("profiles").update({ name: form.name, role: form.role, status: form.status }).eq("id", modal.id);
      setUsers(prev => prev.map(u => u.id === modal.id ? { ...u, name:form.name, role:form.role, status:form.status } : u));
      showToast("✓ User updated"); setModal(null);
    } catch (err) { showToast("❌ " + err.message, "error"); }
    setSaving(false);
  };

  const toggleStatus = async (u) => {
    if (u.id === profile?.id) { showToast("⚠ Cannot deactivate your own account"); return; }
    const s = u.status === "Inactive" ? "Active" : "Inactive";
    await supabase.from("profiles").update({ status: s }).eq("id", u.id);
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: s } : x));
    showToast("✓ User " + (s === "Active" ? "activated" : "deactivated"));
  };

  const deleteUser = async (u) => {
    setSaving(true);
    try {
      await supabase.from("profiles").delete().eq("id", u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      setDeleteConfirm(null); showToast("✓ User deleted");
    } catch (err) { showToast("❌ " + err.message, "error"); }
    setSaving(false);
  };

  return <div>
    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24 }}>
      <div>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:"#e8edf2" }}>User Management</div>
        <div style={{ fontSize:12,color:"#7090a0",marginTop:4 }}>Create and manage users in your organization</div>
      </div>
      <button className="btn" style={{ background:"#00e5a0",color:"#060b10" }} onClick={openAdd}>✉ Invite User</button>
    </div>

    <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24 }}>
      {[{label:"Total Users",value:users.length,icon:"👥",c:"#00e5a0"},{label:"Admins",value:users.filter(u=>u.role==="Admin").length,icon:"🔐",c:"#e05757"},{label:"Operators",value:users.filter(u=>u.role==="Operator").length,icon:"⚙",c:"#f5a623"},{label:"Active",value:users.filter(u=>u.status!=="Inactive").length,icon:"✅",c:"#00e5a0"}].map(s=>(
        <div key={s.label} style={{ background:"#0d1520",border:"1px solid #1a2a3a",borderRadius:12,padding:20 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:10 }}>
            <div style={{ width:36,height:36,borderRadius:9,background:s.c+"18",border:`1px solid ${s.c}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}>{s.icon}</div>
            <div style={{ fontSize:10,color:"#7090a0",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px" }}>{s.label}</div>
          </div>
          <div style={{ fontSize:28,fontWeight:800,color:"#e8edf2",fontFamily:"'Syne',sans-serif" }}>{s.value}</div>
        </div>
      ))}
    </div>

    <div style={{ display:"flex",gap:10,marginBottom:18,flexWrap:"wrap" }}>
      <div style={{ position:"relative",flex:"1 1 240px" }}>
        <span style={{ position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#7090a0" }}>🔍</span>
        <input className="inp" style={{ paddingLeft:34 }} placeholder="Search by name or email…" value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      <div style={{ display:"flex",gap:6 }}>
        {["All","Admin","Operator","Viewer"].map(r=>(
          <button key={r} className="btn" onClick={()=>setRoleFilter(r)} style={{ background:roleFilter===r?"#1a2a3a":"none",border:`1px solid ${roleFilter===r?"#2a3f52":"#1a2a3a"}`,color:roleFilter===r?(ROLE_COLOR[r]||"#e8edf2"):"#a0b4c0",fontSize:12,padding:"7px 14px" }}>{r}</button>
        ))}
      </div>
    </div>

    <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14,marginBottom:28 }}>
      {filtered.map(u=>{
        const isSelf = u.id === profile?.id;
        return (
          <div key={u.id} style={{ background:"#0d1520",border:"1px solid #1a2a3a",borderRadius:12,padding:20,position:"relative" }}>
            {isSelf && <div style={{ position:"absolute",top:14,right:14,background:"#00e5a015",color:"#00e5a0",border:"1px solid #00e5a030",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700 }}>YOU</div>}
            <div style={{ display:"flex",alignItems:"center",gap:14,marginBottom:16 }}>
              <div style={{ width:48,height:48,borderRadius:"50%",background:ROLE_COLOR[u.role]+"22",color:ROLE_COLOR[u.role],display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,flexShrink:0,border:`2px solid ${ROLE_COLOR[u.role]}40` }}>{initials(u.name)}</div>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontWeight:700,fontSize:15,color:"#e8edf2",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{u.name}</div>
                <div style={{ fontSize:12,color:"#7090a0",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{u.email}</div>
              </div>
            </div>
            <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:14 }}>
              <span style={{ background:ROLE_COLOR[u.role]+"18",color:ROLE_COLOR[u.role],border:`1px solid ${ROLE_COLOR[u.role]}38`,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700 }}>{u.role}</span>
              <span style={{ background:u.status==="Inactive"?"#e0575718":"#00e5a018",border:u.status==="Inactive"?"1px solid #e0575730":"1px solid #00e5a030",color:u.status==="Inactive"?"#e05757":"#00e5a0",borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700 }}>{u.status||"Active"}</span>
            </div>
            <div style={{ fontSize:11,color:"#7090a0",padding:"8px 12px",background:"#060b10",borderRadius:8,lineHeight:1.5,marginBottom:14 }}>{ROLE_DESC[u.role]}</div>
            <div style={{ display:"flex",gap:8 }}>
              <button className="btn" style={{ flex:1,background:"#1a2a3a",color:"#c8d6e0",fontSize:12,padding:"8px 0" }} onClick={()=>openEdit(u)}>✏ Edit</button>
              <button className="btn" onClick={()=>toggleStatus(u)} style={{ flex:1,fontSize:12,padding:"8px 0",background:u.status==="Inactive"?"#00e5a020":"#f5a62320",border:`1px solid ${u.status==="Inactive"?"#00e5a040":"#f5a62340"}`,color:u.status==="Inactive"?"#00e5a0":"#f5a623" }}>{u.status==="Inactive"?"✓ Activate":"⏸ Deactivate"}</button>
              {!isSelf && <button className="btn" onClick={()=>setDeleteConfirm(u)} style={{ background:"#e0575720",border:"1px solid #e0575740",color:"#e05757",padding:"8px 12px",fontSize:14 }}>🗑</button>}
            </div>
          </div>
        );
      })}
      {filtered.length===0 && <div style={{ gridColumn:"1/-1",textAlign:"center",padding:"40px 0",color:"#7090a0" }}>No users found</div>}
    </div>

    <div style={{ background:"#0d1520",border:"1px solid #1a2a3a",borderRadius:12,overflow:"hidden" }}>
      {/* Header */}
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",borderBottom:"1px solid #1a2a3a" }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:800,color:"#e8edf2" }}>Role Permissions Matrix</div>
          <div style={{ fontSize:11,color:"#7090a0",marginTop:3 }}>Toggle per-module access for each role. Changes apply immediately.</div>
        </div>
        <button className="btn" onClick={()=>{ setRolePerms(DEFAULT_PERMS); showToast("✓ Reset to defaults"); }} style={{ background:"#1a2a3a",color:"#a0b4c0",fontSize:11,padding:"6px 14px",border:"1px solid #2a3f52" }}>↺ Reset Defaults</button>
      </div>

      {/* Table */}
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:"2px solid #1a2a3a",background:"#090e15" }}>
              <th style={{ padding:"14px 20px",textAlign:"left",fontSize:11,color:"#7090a0",fontWeight:600,minWidth:300,position:"sticky",left:0,background:"#090e15",zIndex:1 }}>
                PERMISSION &amp; PAGES UNLOCKED
              </th>
              {["Admin","Operator","Viewer"].map(r=>(
                <th key={r} style={{ padding:"14px 28px",textAlign:"center",minWidth:130 }}>
                  <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:5 }}>
                    <div style={{ width:34,height:34,borderRadius:"50%",background:ROLE_COLOR[r]+"22",color:ROLE_COLOR[r],display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,border:`2px solid ${ROLE_COLOR[r]}40` }}>{r[0]}</div>
                    <span style={{ fontSize:12,color:ROLE_COLOR[r],fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px" }}>{r}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visiblePermRows.map((grp, gi) => (
              <>
                {/* Group header row */}
                <tr key={"grp-"+gi} style={{ background:"#060b10",borderTop:gi>0?"2px solid #1a2a3a":"none" }}>
                  <td colSpan={4} style={{ padding:"10px 20px" }}>
                    <span style={{ fontSize:11,fontWeight:700,color:"#7090a0",textTransform:"uppercase",letterSpacing:"1px" }}>
                      {grp.icon} &nbsp;{grp.group}
                    </span>
                  </td>
                </tr>
                {/* Permission rows */}
                {grp.perms.map((perm, pi) => {
                  const isLastInGroup = pi === grp.perms.length - 1;
                  return (
                    <tr key={perm.key} style={{ borderBottom:isLastInGroup?"none":"1px solid #0d1520" }}>
                      {/* Permission info */}
                      <td style={{ padding:"13px 20px",position:"sticky",left:0,background:"#0d1520",zIndex:1 }}>
                        <div style={{ fontWeight:600,color:"#c8d6e0",fontSize:13,marginBottom:3 }}>{perm.label}</div>
                        <div style={{ fontSize:11,color:"#7090a0",marginBottom:6 }}>{perm.desc}</div>
                        <div style={{ display:"flex",flexWrap:"wrap",gap:4 }}>
                          {perm.pages.map(pg=>(
                            <span key={pg} style={{ fontSize:10,padding:"1px 7px",borderRadius:4,background:"rgba(91,156,246,.1)",border:"1px solid rgba(91,156,246,.2)",color:"#5b9cf6",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap" }}>
                              {pg}
                            </span>
                          ))}
                        </div>
                      </td>
                      {/* Toggle per role */}
                      {["Admin","Operator","Viewer"].map(r=>{
                        const ADMIN_LOCKED = ["canViewAssets","canViewFacilities","canViewReports","canViewTracking","canManageUsers","canConfig","canManageFacilities","canRegisterAssets","canEditAssets","canDeleteAssets","canApprove","canCheckout","canExport"];
                        const locked = r==="Admin" && ADMIN_LOCKED.includes(perm.key);
                        const val = (rolePerms[r]||{})[perm.key] ?? false;
                        return (
                          <td key={r} style={{ padding:"13px 28px",textAlign:"center",verticalAlign:"middle" }}>
                            {locked
                              ? <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:3 }}>
                                  <div style={{ width:38,height:22,borderRadius:11,background:"#00e5a0",position:"relative" }}>
                                    <div style={{ position:"absolute",top:4,left:20,width:14,height:14,borderRadius:"50%",background:"#fff" }}/>
                                  </div>
                                  <span style={{ fontSize:9,color:"#7090a0" }}>locked</span>
                                </div>
                              : <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:3 }}>
                                  <div
                                    style={{ display:"inline-flex",cursor:"pointer",width:38,height:22,borderRadius:11,background:val?"#00e5a0":"#1a2a3a",position:"relative",transition:"background .2s",border:`1px solid ${val?"#00e5a0":"#2a3f52"}` }}
                                    onClick={()=>setRolePerms(prev=>({...prev,[r]:{...prev[r],[perm.key]:!val}}))}
                                  >
                                    <div style={{ position:"absolute",top:3,left:val?19:3,width:14,height:14,borderRadius:"50%",background:val?"#060b10":"#a0b4c0",transition:"left .2s" }}/>
                                  </div>
                                  <span style={{ fontSize:9,color:val?"#00e5a0":"#7090a0",fontWeight:600 }}>{val?"ON":"OFF"}</span>
                                </div>
                            }
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer legend */}
      <div style={{ padding:"12px 20px",borderTop:"1px solid #1a2a3a",background:"#090e15",display:"flex",gap:20,flexWrap:"wrap" }}>
        <div style={{ display:"flex",alignItems:"center",gap:6,fontSize:11,color:"#7090a0" }}>
          <div style={{ width:28,height:16,borderRadius:8,background:"#00e5a0",position:"relative",flexShrink:0 }}>
            <div style={{ position:"absolute",top:3,left:14,width:10,height:10,borderRadius:"50%",background:"#060b10" }}/>
          </div>
          Permission granted
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:6,fontSize:11,color:"#7090a0" }}>
          <div style={{ width:28,height:16,borderRadius:8,background:"#1a2a3a",border:"1px solid #2a3f52",position:"relative",flexShrink:0 }}>
            <div style={{ position:"absolute",top:3,left:3,width:10,height:10,borderRadius:"50%",background:"#a0b4c0" }}/>
          </div>
          Permission denied
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:6,fontSize:11,color:"#7090a0" }}>
          <span style={{ fontSize:9,padding:"1px 7px",borderRadius:4,background:"rgba(91,156,246,.1)",border:"1px solid rgba(91,156,246,.2)",color:"#5b9cf6",fontFamily:"'IBM Plex Mono',monospace" }}>📦 Page</span>
          Pages this permission unlocks
        </div>
        <div style={{ marginLeft:"auto",fontSize:11,color:"#7090a0" }}>
          Admin permissions are fixed and cannot be reduced
        </div>
      </div>
    </div>

    {modal==="add" && <Modal title="Invite New User" onClose={()=>setModal(null)}>
      <div style={{ background:'#00e5a010',border:'1px solid #00e5a030',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#7090a0' }}>
        📧 An invite email will be sent to the user. They click the link to set their own password and log in.
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
        <div style={{ gridColumn:"1/-1" }}><Lbl>Full Name *</Lbl><input className="inp" value={form.name||""} placeholder="e.g. Nur Aina Binti Ahmad" onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
        <div style={{ gridColumn:"1/-1" }}><Lbl>Email Address *</Lbl><input className="inp" type="email" value={form.email||""} placeholder="user@company.com" onChange={e=>setForm(f=>({...f,email:e.target.value}))}/></div>
        <div style={{ gridColumn:"1/-1" }}><Lbl>Role *</Lbl>
          <select className="sel" style={{ width:"100%" }} value={form.role||"Operator"} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
            {["Admin","Operator","Viewer"].map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display:"flex",gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:"none",border:"1px solid #1a2a3a",color:"#a0b4c0" }} onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:"#00e5a0",color:"#060b10" }} onClick={saveAdd} disabled={saving||!form.name||!form.email}>{saving?"Sending Invite…":"✉ Send Invite"}</button>
      </div>
    </Modal>}

    {modal && modal!=="add" && <Modal title="Edit User" sub={modal.email} onClose={()=>setModal(null)}>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
        <div style={{ gridColumn:"1/-1" }}><Lbl>Full Name *</Lbl><input className="inp" value={form.name||""} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
        <div><Lbl>Role</Lbl>
          <select className="sel" style={{ width:"100%" }} value={form.role||"Operator"} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
            {["Admin","Operator","Viewer"].map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
        <div><Lbl>Status</Lbl>
          <select className="sel" style={{ width:"100%" }} value={form.status||"Active"} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
            <option>Active</option><option>Inactive</option>
          </select>
        </div>
      </div>
      <div style={{ display:"flex",gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:"none",border:"1px solid #1a2a3a",color:"#a0b4c0" }} onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:"#00e5a0",color:"#060b10" }} onClick={saveEdit} disabled={saving}>{saving?"Saving…":"Save Changes"}</button>
      </div>
    </Modal>}

    {deleteConfirm && <Modal title="Delete User" onClose={()=>setDeleteConfirm(null)}>
      <div style={{ textAlign:"center",padding:"10px 0 20px" }}>
        <div style={{ fontSize:40,marginBottom:12 }}>🗑</div>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:18,fontWeight:800,color:"#e8edf2",marginBottom:8 }}>Delete {deleteConfirm.name}?</div>
        <div style={{ fontSize:13,color:"#7090a0",marginBottom:16 }}>{deleteConfirm.email}</div>
      </div>
      <div style={{ display:"flex",gap:10 }}>
        <button className="btn" style={{ flex:1,background:"none",border:"1px solid #1a2a3a",color:"#a0b4c0" }} onClick={()=>setDeleteConfirm(null)}>Cancel</button>
        <button className="btn" style={{ flex:1,background:"#e05757",color:"#fff" }} onClick={()=>deleteUser(deleteConfirm)} disabled={saving}>{saving?"Deleting…":"Delete User"}</button>
      </div>
    </Modal>}
  </div>;
}
